import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../lib/api-client";
import { useAuth } from "../auth/useAuth";

const PAGE_SIZE = 25;

// None of the paginated list endpoints return a total count, so "hasNext" is
// determined by fetching one extra row past the page size and checking
// whether it exists, rather than computing a page total.
export function usePaginatedResource<T>(
  fetcher: (limit: number, offset: number) => Promise<T[]>,
  deps: unknown[],
) {
  const { logout } = useAuth();
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<T[]>([]);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const offset = (page - 1) * PAGE_SIZE;
    return fetcher(PAGE_SIZE + 1, offset)
      .then((result) => {
        setHasNext(result.length > PAGE_SIZE);
        setRows(result.slice(0, PAGE_SIZE));
        setLoading(false);
      })
      .catch((requestError: unknown) => {
        if (requestError instanceof ApiError && requestError.status === 401) {
          logout();
          return;
        }
        setError(requestError instanceof Error ? requestError.message : "Failed to load data");
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, ...deps]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return {
    rows,
    loading,
    error,
    page,
    hasNext,
    refresh: load,
    nextPage: () => setPage((p) => p + 1),
    prevPage: () => setPage((p) => Math.max(1, p - 1)),
  };
}
